import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { UserService } from '../services/index.js';
import { tokenManager } from '../services/api.js';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      userId: null,
      outletId: null,
      outletName: null,
      userName: null,
      accessType: null,
      phone: null,
      menuId: null,
      menuItems: [],
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,

      /**
       * Login with phone + PIN via the backend API
       */
      login: async (phone, pin) => {
        set({ isLoading: true });
        try {
          // Clean phone number — remove country code prefix if present
          let cleanPhone = phone.replace(/\D/g, '');
          if (cleanPhone.startsWith('91') && cleanPhone.length > 10) {
            cleanPhone = cleanPhone.substring(2);
          }

          // Call backend API — this stores tokens via tokenManager
          const data = await UserService.login(cleanPhone, pin);

          if (!data || !data.user) {
            throw new Error('Invalid phone number or PIN');
          }

          const user = data.user;
          console.log('[AuthStore] Login successful:', user.name);

          // Now fetch the outlet name
          let outletName = '';
          try {
            const { OutletService } = await import('../services/outletService.js');
            outletName = await OutletService.fetchOutletName(user.outlet_id);
          } catch (e) {
            console.warn('[AuthStore] Could not fetch outlet name:', e);
          }

          set({
            userId: user.user_id,
            outletId: user.outlet_id,
            outletName: outletName || user.outlet_id,
            userName: user.name,
            accessType: user.access_type,
            phone: user.phone,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          });
        } catch (error) {
          set({ isLoading: false });
          // Re-throw with a user-friendly message
          const msg = error.message || 'Login failed. Please try again.';
          throw new Error(msg);
        }
      },

      /**
       * Logout — clear tokens and state
       */
      logout: async () => {
        try {
          await UserService.logout();
        } catch (e) {
          // Ignore — logout is mainly client-side
        }

        set({
          userId: null,
          outletId: null,
          outletName: null,
          userName: null,
          accessType: null,
          phone: null,
          menuId: null,
          menuItems: [],
          isAuthenticated: false,
          isLoading: false,
          isInitialized: false,
        });
      },

      /**
       * Check if user has a valid session (tokens exist and refresh works)
       * Called on app startup to restore session
       */
      checkAuth: async () => {
        const state = get();

        // Prevent multiple simultaneous calls
        if (state.isLoading || state.isInitialized) {
          console.log('[AuthStore] checkAuth already in progress or initialized, skipping');
          return;
        }

        console.log('[AuthStore] Starting authentication check...');
        set({ isLoading: true });

        try {
          // Check if we have tokens stored
          if (!tokenManager.hasTokens()) {
            console.log('[AuthStore] No tokens found — user needs to login');
            set({
              isLoading: false,
              isAuthenticated: false,
              isInitialized: true,
            });
            return;
          }

          // If token is expired, try to refresh it
          if (tokenManager.isTokenExpired()) {
            console.log('[AuthStore] Token expired, attempting refresh...');
            try {
              await UserService.refreshToken();
              console.log('[AuthStore] Token refreshed successfully');
            } catch (refreshError) {
              console.error('[AuthStore] Token refresh failed:', refreshError);
              tokenManager.clearTokens();
              set({
                isLoading: false,
                isAuthenticated: false,
                isInitialized: true,
              });
              return;
            }
          }

          // We have a valid token — check persisted state
          const persisted = get();
          if (persisted.userId && persisted.outletId) {
            console.log('[AuthStore] Restored session for:', persisted.userName || persisted.userId);
            set({
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
            });
          } else {
            // Tokens exist but no persisted user data — user might have cleared storage
            // We can't recover the user info without re-logging in
            console.log('[AuthStore] Tokens exist but no user data — clearing');
            tokenManager.clearTokens();
            set({
              isLoading: false,
              isAuthenticated: false,
              isInitialized: true,
            });
          }
        } catch (error) {
          console.error('[AuthStore] Auth check failed:', error);
          set({
            isLoading: false,
            isAuthenticated: false,
            isInitialized: true,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        userId: state.userId,
        outletId: state.outletId,
        outletName: state.outletName,
        userName: state.userName,
        accessType: state.accessType,
        phone: state.phone,
        menuId: state.menuId,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
